import React, { useState } from 'react';
import { Sun, Moon, Monitor, Menu, X } from 'lucide-react';
import Logo from './Logo';

const NAV_LINKS = [
  { label: 'Blog',    href: '/blog'        },
  { label: 'Contact', href: '/contact'     },
  { label: 'Shop',    href: '/shop'        },
  { label: 'Pricing', href: '/study-plans' },
];

const THEME_ICON  = { day: Sun, night: Moon, auto: Monitor };
const THEME_CYCLE = { day: 'night', night: 'auto', auto: 'day' };

// Dark nav + footer that matches the LandingPage, used for all public pages.
export default function PublicLayout({ children, onSignIn, currentTheme, onThemeChange }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const mode = currentTheme || 'night';
  const ThemeIcon = THEME_ICON[mode] || Moon;

  return (
    <div className="min-h-screen flex flex-col font-sora antialiased">

      {/* ── Navbar — dark glass, fixed ───────────────────────────────── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-16 py-5"
        style={{
          background: 'rgba(5,5,7,0.92)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
        }}
      >
        <a href="/">
          <Logo size="md" style={{ '--text-primary': '#dde0e8' }} />
        </a>

        <div className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map(l => (
            <a
              key={l.label}
              href={l.href}
              className="text-sm uppercase tracking-widest transition-colors"
              style={{ color: 'rgba(221,224,232,0.6)', fontFamily: "'Sora','Inter',sans-serif" }}
              onMouseEnter={e => { e.currentTarget.style.color = '#dde0e8'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'rgba(221,224,232,0.6)'; }}
            >
              {l.label}
            </a>
          ))}
        </div>

        {/* Desktop right: theme icon + sign in */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => onThemeChange && onThemeChange(THEME_CYCLE[mode])}
            aria-label="Toggle theme"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#dde0e8', flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          >
            <ThemeIcon size={15} strokeWidth={2.2} />
          </button>
          <button
            onClick={onSignIn}
            className="active:scale-[0.97] uppercase text-xs tracking-widest px-6 py-2.5 rounded-lg transition-all"
            style={{
              background: 'rgba(255,255,255,0.08)',
              color: '#dde0e8',
              fontFamily: "'Sora','Inter',sans-serif",
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          >
            Sign In
          </button>
        </div>

        {/* Mobile: theme + burger */}
        <div className="flex md:hidden items-center gap-2">
          <button
            onClick={() => onThemeChange && onThemeChange(THEME_CYCLE[mode])}
            aria-label="Toggle theme"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#dde0e8', flexShrink: 0 }}
          >
            <ThemeIcon size={15} strokeWidth={2.2} />
          </button>
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#dde0e8', flexShrink: 0 }}
          >
            <Menu size={18} strokeWidth={2.2} />
          </button>
        </div>
      </nav>

      {/* Mobile full-screen menu */}
      {menuOpen && (
        <div
          className="md:hidden"
          style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(5,5,7,0.97)', backdropFilter: 'blur(14px)', display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px' }}>
            <a href="/"><Logo size="md" style={{ '--text-primary': '#dde0e8' }} /></a>
            <button
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#dde0e8' }}
            >
              <X size={18} />
            </button>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '0 24px 40px' }}>
            {NAV_LINKS.map(l => (
              <a
                key={l.label}
                href={l.href}
                onClick={() => setMenuOpen(false)}
                style={{ width: '100%', textAlign: 'center', padding: '14px 0', fontSize: '1rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(221,224,232,0.8)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.07)' }}
              >
                {l.label}
              </a>
            ))}
            <button
              onClick={() => { setMenuOpen(false); onSignIn && onSignIn(); }}
              style={{ marginTop: '28px', width: '100%', background: '#04EC01', color: '#050507', fontSize: '0.8rem', fontWeight: 900, letterSpacing: '0.16em', textTransform: 'uppercase', padding: '14px 36px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}
            >
              Sign In
            </button>
          </div>
        </div>
      )}

      {/* ── Page content — pushed below the fixed 70px nav ───────────── */}
      <main className="flex-grow" style={{ paddingTop: '70px' }}>
        {children}
      </main>

      {/* ── Footer — matches LandingPage footer exactly ───────────────── */}
      <footer className="bg-[#050507] border-t border-white/[0.06] py-8 px-8 md:px-16 flex flex-col sm:flex-row items-center justify-between gap-4">
        <Logo size="sm" style={{ '--text-primary': '#dde0e8' }} />
        <p className="text-xs text-white/25 font-light">
          © 2026 Go4Nihongo · Built for JLPT learners worldwide
        </p>
        <div className="flex gap-6 text-xs font-medium text-white/30">
          <button
            onClick={onSignIn}
            className="hover:text-white transition-colors uppercase tracking-widest bg-transparent border-none cursor-pointer"
          >
            Login
          </button>
          <a href="/privacy-policy" className="hover:text-white transition-colors uppercase tracking-widest">Privacy</a>
          <a href="/terms-of-service" className="hover:text-white transition-colors uppercase tracking-widest">Terms</a>
        </div>
      </footer>

    </div>
  );
}
